import { useState, useEffect } from 'react'
import {
  Settings,
  Bell,
  Shield,
  Mail,
  Palette,
  Save,
} from 'lucide-react'
import { apiService } from '../services/api'
import { useToast } from '../hooks/useToast'
import Button from '../components/common/Button'
import Input from '../components/common/Input'
import Card from '../components/common/Card'
import type { SystemSettings as SystemSettingsType } from '../types'

interface SettingsState {
  general: {
    siteName: string
    siteDescription: string
    siteUrl: string
    timezone: string
    dateFormat: string
  }
  email: {
    smtpHost: string
    smtpPort: string
    smtpUser: string
    smtpPassword: string
    fromEmail: string
    fromName: string
  }
  notification: {
    emailOnNewSubmission: boolean
    emailOnNewUser: boolean
    emailOnSystemError: boolean
    dailyDigest: boolean
  }
  security: {
    maxLoginAttempts: number
    lockoutDuration: number
    passwordMinLength: number
    requireStrongPassword: boolean
    twoFactorAuth: boolean
  }
  appearance: {
    primaryColor: string
    logo: string
    favicon: string
    customCss: string
  }
}

export default function SystemSettings() {
  const [activeTab, setActiveTab] = useState('general')
  const [saving, setSaving] = useState(false)
  const { showToast } = useToast()

  const [settings, setSettings] = useState<SettingsState>({
    general: {
      siteName: '表单预约管理系统',
      siteDescription: '一个强大的多用户自定义表单预约管理平台',
      siteUrl: 'https://example.com',
      timezone: 'Asia/Shanghai',
      dateFormat: 'YYYY-MM-DD HH:mm:ss',
    },
    email: {
      smtpHost: '',
      smtpPort: '587',
      smtpUser: '',
      smtpPassword: '',
      fromEmail: '',
      fromName: '',
    },
    notification: {
      emailOnNewSubmission: true,
      emailOnNewUser: true,
      emailOnSystemError: true,
      dailyDigest: false,
    },
    security: {
      maxLoginAttempts: 5,
      lockoutDuration: 30,
      passwordMinLength: 8,
      requireStrongPassword: true,
      twoFactorAuth: false,
    },
    appearance: {
      primaryColor: '#2563eb',
      logo: '',
      favicon: '',
      customCss: '',
    },
  })

  const fetchSettings = async () => {
    try {
      const response = await apiService.getSystemSettings()
      if (response.data?.settings) {
        setSettings((prev) => ({ ...prev, ...response.data?.settings }))
      }
    } catch (error) {
      showToast('获取设置失败', 'error')
    }
  }

  useEffect(() => {
    fetchSettings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSave = async () => {
    try {
      setSaving(true)
      // Convert SettingsState to Partial<SystemSettingsType> format
      const systemSettings: Partial<SystemSettingsType> = {
        siteName: settings.general.siteName,
        siteDescription: settings.general.siteDescription,
        timezone: settings.general.timezone,
        dateFormat: settings.general.dateFormat,
        emailSettings: {
          smtpHost: settings.email.smtpHost,
          smtpPort: parseInt(settings.email.smtpPort) || 587,
          smtpUser: settings.email.smtpUser,
          smtpPassword: settings.email.smtpPassword,
          fromEmail: settings.email.fromEmail,
          fromName: settings.email.fromName,
          encryption: 'tls' as const,
        },
        notificationSettings: {
          emailOnNewOrder: settings.notification.emailOnNewSubmission,
          emailOnFormSubmit: settings.notification.emailOnNewSubmission,
          emailOnUserRegister: settings.notification.emailOnNewUser,
          slackWebhook: '',
          discordWebhook: '',
        },
        securitySettings: {
          maxLoginAttempts: settings.security.maxLoginAttempts,
          lockoutDuration: settings.security.lockoutDuration,
          passwordMinLength: settings.security.passwordMinLength,
          twoFactorAuth: settings.security.twoFactorAuth,
          requireEmailVerification: false,
          passwordRequireNumbers: settings.security.requireStrongPassword,
          passwordRequireSymbols: settings.security.requireStrongPassword,
          sessionTimeout: 30,
        },
      }
      await apiService.updateSystemSettings(systemSettings)
      showToast('设置保存成功', 'success')
    } catch (error) {
      showToast('设置保存失败', 'error')
    } finally {
      setSaving(false)
    }
  }

  const tabs = [
    { id: 'general', label: '常规设置', icon: Settings },
    { id: 'email', label: '邮件设置', icon: Mail },
    { id: 'notification', label: '通知设置', icon: Bell },
    { id: 'security', label: '安全设置', icon: Shield },
    { id: 'appearance', label: '外观设置', icon: Palette },
  ]

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <Input
        label="网站名称"
        value={settings.general.siteName}
        onChange={(e) =>
          setSettings({
            ...settings,
            general: { ...settings.general, siteName: e.target.value },
          })
        }
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          网站描述
        </label>
        <textarea
          value={settings.general.siteDescription}
          onChange={(e) =>
            setSettings({
              ...settings,
              general: { ...settings.general, siteDescription: e.target.value },
            })
          }
          rows={3}
          className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <Input
        label="网站URL"
        value={settings.general.siteUrl}
        onChange={(e) =>
          setSettings({
            ...settings,
            general: { ...settings.general, siteUrl: e.target.value },
          })
        }
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          时区
        </label>
        <select
          value={settings.general.timezone}
          onChange={(e) =>
            setSettings({
              ...settings,
              general: { ...settings.general, timezone: e.target.value },
            })
          }
          className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="Asia/Shanghai">Asia/Shanghai (北京时间)</option>
          <option value="Asia/Hong_Kong">Asia/Hong_Kong (香港时间)</option>
          <option value="Asia/Taipei">Asia/Taipei (台北时间)</option>
          <option value="Asia/Tokyo">Asia/Tokyo (东京时间)</option>
          <option value="Asia/Seoul">Asia/Seoul (首尔时间)</option>
          <option value="America/New_York">America/New_York (纽约时间)</option>
          <option value="America/Los_Angeles">America/Los_Angeles (洛杉矶时间)</option>
          <option value="Europe/London">Europe/London (伦敦时间)</option>
          <option value="Europe/Paris">Europe/Paris (巴黎时间)</option>
          <option value="UTC">UTC (协调世界时)</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          日期格式
        </label>
        <select
          value={settings.general.dateFormat}
          onChange={(e) =>
            setSettings({
              ...settings,
              general: { ...settings.general, dateFormat: e.target.value },
            })
          }
          className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="YYYY-MM-DD HH:mm:ss">YYYY-MM-DD HH:mm:ss</option>
          <option value="YYYY/MM/DD HH:mm:ss">YYYY/MM/DD HH:mm:ss</option>
          <option value="DD-MM-YYYY HH:mm:ss">DD-MM-YYYY HH:mm:ss</option>
          <option value="DD/MM/YYYY HH:mm:ss">DD/MM/YYYY HH:mm:ss</option>
          <option value="MM-DD-YYYY HH:mm:ss">MM-DD-YYYY HH:mm:ss</option>
        </select>
      </div>
    </div>
  )

  const renderEmailSettings = () => (
    <div className="space-y-6">
      <Input
        label="SMTP 服务器"
        value={settings.email.smtpHost}
        onChange={(e) =>
          setSettings({
            ...settings,
            email: { ...settings.email, smtpHost: e.target.value },
          })
        }
        placeholder="smtp.example.com"
      />
      <Input
        label="SMTP 端口"
        value={settings.email.smtpPort}
        onChange={(e) =>
          setSettings({
            ...settings,
            email: { ...settings.email, smtpPort: e.target.value },
          })
        }
        placeholder="587"
      />
      <Input
        label="SMTP 用户名"
        value={settings.email.smtpUser}
        onChange={(e) =>
          setSettings({
            ...settings,
            email: { ...settings.email, smtpUser: e.target.value },
          })
        }
      />
      <Input
        label="SMTP 密码"
        type="password"
        value={settings.email.smtpPassword}
        onChange={(e) =>
          setSettings({
            ...settings,
            email: { ...settings.email, smtpPassword: e.target.value },
          })
        }
      />
      <Input
        label="发件人邮箱"
        value={settings.email.fromEmail}
        onChange={(e) =>
          setSettings({
            ...settings,
            email: { ...settings.email, fromEmail: e.target.value },
          })
        }
        placeholder="noreply@example.com"
      />
      <Input
        label="发件人名称"
        value={settings.email.fromName}
        onChange={(e) =>
          setSettings({
            ...settings,
            email: { ...settings.email, fromName: e.target.value },
          })
        }
        placeholder="表单预约管理系统"
      />
    </div>
  )

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={settings.notification.emailOnNewSubmission}
            onChange={(e) =>
              setSettings({
                ...settings,
                notification: {
                  ...settings.notification,
                  emailOnNewSubmission: e.target.checked,
                },
              })
            }
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-gray-700 dark:text-gray-300">新表单提交时发送邮件通知</span>
        </label>
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={settings.notification.emailOnNewUser}
            onChange={(e) =>
              setSettings({
                ...settings,
                notification: {
                  ...settings.notification,
                  emailOnNewUser: e.target.checked,
                },
              })
            }
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-gray-700 dark:text-gray-300">新用户注册时发送邮件通知</span>
        </label>
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={settings.notification.emailOnSystemError}
            onChange={(e) =>
              setSettings({
                ...settings,
                notification: {
                  ...settings.notification,
                  emailOnSystemError: e.target.checked,
                },
              })
            }
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-gray-700 dark:text-gray-300">系统错误时发送邮件通知</span>
        </label>
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={settings.notification.dailyDigest}
            onChange={(e) =>
              setSettings({
                ...settings,
                notification: {
                  ...settings.notification,
                  dailyDigest: e.target.checked,
                },
              })
            }
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-gray-700 dark:text-gray-300">每日摘要邮件</span>
        </label>
      </div>
    </div>
  )

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <Input
        label="最大登录尝试次数"
        type="number"
        value={settings.security.maxLoginAttempts}
        onChange={(e) =>
          setSettings({
            ...settings,
            security: {
              ...settings.security,
              maxLoginAttempts: Number(e.target.value),
            },
          })
        }
      />
      <Input
        label="锁定时间（分钟）"
        type="number"
        value={settings.security.lockoutDuration}
        onChange={(e) =>
          setSettings({
            ...settings,
            security: {
              ...settings.security,
              lockoutDuration: Number(e.target.value),
            },
          })
        }
      />
      <Input
        label="密码最小长度"
        type="number"
        value={settings.security.passwordMinLength}
        onChange={(e) =>
          setSettings({
            ...settings,
            security: {
              ...settings.security,
              passwordMinLength: Number(e.target.value),
            },
          })
        }
      />
      <label className="flex items-center space-x-3">
        <input
          type="checkbox"
          checked={settings.security.requireStrongPassword}
          onChange={(e) =>
            setSettings({
              ...settings,
              security: {
                ...settings.security,
                requireStrongPassword: e.target.checked,
              },
            })
          }
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <span className="text-gray-700 dark:text-gray-300">要求强密码（包含大小写字母、数字和特殊字符）</span>
      </label>
      <label className="flex items-center space-x-3">
        <input
          type="checkbox"
          checked={settings.security.twoFactorAuth}
          onChange={(e) =>
            setSettings({
              ...settings,
              security: {
                ...settings.security,
                twoFactorAuth: e.target.checked,
              },
            })
          }
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <span className="text-gray-700 dark:text-gray-300">启用双因素认证</span>
      </label>
    </div>
  )

  const renderAppearanceSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          主题色
        </label>
        <div className="flex items-center space-x-3">
          <input
            type="color"
            value={settings.appearance.primaryColor}
            onChange={(e) =>
              setSettings({
                ...settings,
                appearance: { ...settings.appearance, primaryColor: e.target.value },
              })
            }
            className="w-12 h-12 rounded-lg border border-gray-300 dark:border-gray-600"
          />
          <Input
            value={settings.appearance.primaryColor}
            onChange={(e) =>
              setSettings({
                ...settings,
                appearance: { ...settings.appearance, primaryColor: e.target.value },
              })
            }
            className="flex-1"
          />
        </div>
      </div>
      <Input
        label="Logo URL"
        value={settings.appearance.logo}
        onChange={(e) =>
          setSettings({
            ...settings,
            appearance: { ...settings.appearance, logo: e.target.value },
          })
        }
        placeholder="https://example.com/logo.png"
      />
      <Input
        label="Favicon URL"
        value={settings.appearance.favicon}
        onChange={(e) =>
          setSettings({
            ...settings,
            appearance: { ...settings.appearance, favicon: e.target.value },
          })
        }
        placeholder="https://example.com/favicon.ico"
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          自定义 CSS
        </label>
        <textarea
          value={settings.appearance.customCss}
          onChange={(e) =>
            setSettings({
              ...settings,
              appearance: { ...settings.appearance, customCss: e.target.value },
            })
          }
          rows={6}
          className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="/* 在此输入自定义 CSS */"
        />
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralSettings()
      case 'email':
        return renderEmailSettings()
      case 'notification':
        return renderNotificationSettings()
      case 'security':
        return renderSecuritySettings()
      case 'appearance':
        return renderAppearanceSettings()
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">系统设置</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">配置系统各项参数和功能</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* 左侧导航 */}
        <div className="lg:w-64">
          <Card className="sticky top-6">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                )
              })}
            </nav>
          </Card>
        </div>

        {/* 右侧内容 */}
        <div className="flex-1">
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                {tabs.find((t) => t.id === activeTab)?.label}
              </h2>
              {renderTabContent()}

              <div className="flex justify-end mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <Button variant="primary" onClick={handleSave} loading={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  保存设置
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
